import { Filter, GlProgram } from "pixi.js";
import { FILTER_VERTEX_GLSL, FRAME_LOCAL_UV_GLSL } from "./filterVertex";

export const SHINE_SWEEP_FRAGMENT_GLSL = `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform highp vec4 uInputSize;
uniform highp vec4 uOutputFrame;
uniform vec4 uShineShape;   // x = highlight (peak gain), y = tilt, z = width, w = softness
uniform vec4 uShineMotion;  // x = progress (0..1), y = autoSpeed (sweeps/sec; 0 = driven), z = pause (s), w = phaseOffset
uniform float uTime;

${FRAME_LOCAL_UV_GLSL}

void main(void) {
  vec4 o = texture(uTexture, vTextureCoord);
  vec2 luv = frameLocalUv(vTextureCoord);

  float highlight = uShineShape.x;
  float tilt      = uShineShape.y;
  float width     = uShineShape.z;
  float softness  = uShineShape.w;

  float progress;
  if (uShineMotion.y > 0.0) {
    float sweepDur = 1.0 / uShineMotion.y;
    float total    = sweepDur + uShineMotion.z;
    float t        = mod(uTime + uShineMotion.w * total, total);
    progress = clamp(t / sweepDur, 0.0, 1.0);
  } else {
    progress = uShineMotion.x;
  }

  float clearance  = width + softness + abs(tilt) * 0.5;
  float bandCenter = mix(-clearance, 1.0 + clearance, progress);
  float lane = luv.x + (luv.y - 0.5) * tilt;
  float band = 1.0 - smoothstep(width, width + softness, abs(lane - bandCenter));
  o.rgb *= 1.0 + max(highlight, 0.0) * band;
  finalColor = o;
}
`;

export interface ShineSweepOptions {
  highlight?: number;
  tilt?: number;
  width?: number;
  softness?: number;
  progress?: number;
  /** Sweeps per second when self-animating; 0 = driven by `progress`. */
  autoSpeed?: number;
  /** Dark gap in seconds between self-animated sweeps. */
  pause?: number;
  phaseOffset?: number;
}

/**
 * Port of `SpriteShineSweep.effect`: a tilted glint band travelling across the
 * object's frame. Driven (`progress` 0→1 by the runner) or self-animating
 * (`autoSpeed` > 0: advance `time` on engine time, from a runner's
 * `tickInScope` or the `PresentationTicker`, never the app ticker, so the
 * sweep pauses and frame-steps with every other timeline). Defaults are the
 * shipped `.mtl` values, except `autoSpeed`: 0 (driven) where the material
 * has 2.15.
 */
export class ShineSweepFilter extends Filter {
  public constructor(options: ShineSweepOptions = {}) {
    super({
      glProgram: GlProgram.from({ vertex: FILTER_VERTEX_GLSL, fragment: SHINE_SWEEP_FRAGMENT_GLSL, name: "slot-shine-sweep", preferredFragmentPrecision: "highp" }),
      resources: {
        shineUniforms: {
          uShineShape: { value: [options.highlight ?? 1.99, options.tilt ?? -0.5, options.width ?? 0.1, options.softness ?? 0.2], type: "vec4<f32>" },
          uShineMotion: { value: [options.progress ?? 0, options.autoSpeed ?? 0, options.pause ?? 4, options.phaseOffset ?? 0], type: "vec4<f32>" },
          uTime: { value: 0, type: "f32" },
        },
      },
    });
  }

  private get shape(): Float32Array {
    return this.resources.shineUniforms.uniforms.uShineShape as Float32Array;
  }
  private get motion(): Float32Array {
    return this.resources.shineUniforms.uniforms.uShineMotion as Float32Array;
  }

  public set highlight(v: number) {
    this.shape[0] = v;
  }
  public set tilt(v: number) {
    this.shape[1] = v;
  }
  public set width(v: number) {
    this.shape[2] = v;
  }
  public set softness(v: number) {
    this.shape[3] = v;
  }
  public get progress(): number {
    return this.motion[0]!;
  }
  public set progress(v: number) {
    this.motion[0] = v;
  }
  public set autoSpeed(v: number) {
    this.motion[1] = v;
  }
  public set pause(v: number) {
    this.motion[2] = v;
  }
  public set phaseOffset(v: number) {
    this.motion[3] = v;
  }
  /** Seconds; only read when `autoSpeed` > 0. */
  public set time(v: number) {
    this.resources.shineUniforms.uniforms.uTime = v;
  }
}
